import React, { useState, useEffect } from 'react';
import { ArrowLeft, ShoppingBag, Minus, Plus, Bell, Droplets, Info, List } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { hasDiscount, priceBefore } from '../utils/discount';
import { formatPrice } from '../utils/price';
import CareBadge from './CareBadge/CareBadge';

// Приёмка: в открытой карточке сразу выбран первый доступный размер
const firstAvailableId = (product) => {
  const v = (product.variants || []).find(x => (x.quantity ?? 0) > 0);
  return v ? v.id : null;
};

export default function ProductDetail({ product, onClose }) {
  const { addToCart, cart, updateCartQuantity, removeFromCart, toggleNotification, isNotified } = useApp();
  const [notifError, setNotifError] = useState('');

  const variants = product.variants || [];
  const hasVariants = variants.length > 0;
  const [selectedId, setSelectedId] = useState(() => firstAvailableId(product));
  const selected = hasVariants ? (variants.find(v => v.id === selectedId) || null) : null;

  // Карточка может смениться без размонтирования (глубокая ссылка из бота)
  useEffect(() => {
    setSelectedId(firstAvailableId(product));
  }, [product.id]);

  // Приёмка: пока карточка открыта, фон не скроллится — иначе видно два
  // скролл-бара, а долистав карточку вниз, уезжает каталог под ней
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const inCart = cart.find(
    item => item.id === product.id
      && (item.variant_id || null) === (selected ? selected.id : null)
  );
  const quantity = inCart ? inCart.quantity : 0;
  const isOutOfStock = !product.in_stock || product.quantity <= 0;
  const notified = isNotified(product.id);

  // Цена под названием: выбранный размер — точная; до выбора — «от X ₽» (products.price = min)
  const priceLabel = hasVariants
    ? (selected ? `${formatPrice(selected.price)} ₽` : `от ${formatPrice(product.price)} ₽`)
    : `${formatPrice(product.price)} ₽`;
  // «Цена до скидки» — от цены выбранного размера (или min, если размер не выбран)
  const discountBasePrice = hasVariants && selected ? selected.price : product.price;

  const handleNotification = async () => {
    setNotifError('');
    const result = await toggleNotification(product);
    if (!result.success) {
      setNotifError(result.error);
      setTimeout(() => setNotifError(''), 3000);
    }
  };

  const handleAdd = () => {
    // Размер уже выбран по умолчанию; без выбора (все распроданы) футер
    // показывает «Сообщить о поступлении», сюда не попадаем
    if (hasVariants && !selected) return;
    addToCart(product, 1, selected);
  };

  const handleDecrease = () => {
    if (quantity <= 1) {
      removeFromCart(product.id, selected ? selected.id : null);
    } else {
      updateCartQuantity(product.id, quantity - 1, selected ? selected.id : null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-cream">
      {/* Шапка */}
      <header className="shrink-0 border-b border-border-warm bg-cream/90 px-4 py-3 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-xl bg-cream-card text-green-muted">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-green-dark truncate">{product.name}</div>
          </div>
        </div>
      </header>

      {/* Скролл-контент */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-4">
        {/* Фото */}
        <div className="aspect-square w-full overflow-hidden rounded-2xl bg-cream-card">
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-6xl">🌱</div>
          )}
        </div>

        {/* Название, латынь, цена */}
        <div>
          <h1 className="text-lg font-bold text-green-dark">{product.name}</h1>
          {product.latin_name && (
            <p className="mt-0.5 text-sm italic text-green-muted">{product.latin_name}</p>
          )}
          {!!product.care_level && (
            <div className="mt-2">
              <CareBadge careLevel={product.care_level} />
            </div>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {hasDiscount(product) && !isOutOfStock && (
              <span className="text-sm text-green-muted line-through">
                {formatPrice(priceBefore(discountBasePrice, product.discount_percent))} ₽
              </span>
            )}
            {hasDiscount(product) && !isOutOfStock && (
              <span className="rounded-full bg-[#D64545]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#D64545]">
                −{product.discount_percent}%
              </span>
            )}
            {hasDiscount(product) && Number(product.discount_percent) === 50 && !isOutOfStock && (
              <span aria-label="Распродажа" title="Распродажа">🔥</span>
            )}
            <span className="text-xl font-bold text-green-dark">
              {isOutOfStock ? 'Нет в наличии' : priceLabel}
            </span>
          </div>
        </div>

        {/* Чипы размеров: ТОЛЬКО подпись, остаток 0 — приспущен и не нажимается */}
        {hasVariants && (
          <div className="flex flex-wrap gap-2">
            {variants.map(v => {
              const out = (v.quantity ?? 0) <= 0;
              return (
                <button
                  key={v.id}
                  onClick={() => { if (!out) setSelectedId(v.id); }}
                  disabled={out}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold transition active:scale-95 ${
                    out
                      ? 'cursor-not-allowed bg-cream-card text-green-muted/60 opacity-50 line-through ring-1 ring-border-warm'
                      : selected?.id === v.id
                        ? 'bg-green-primary text-white shadow-sm'
                        : 'bg-cream-card text-green-dark ring-1 ring-border-warm hover:bg-green-sage/10'
                  }`}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Характеристики */}
        {product.characteristics && Object.keys(product.characteristics).length > 0 && (
          <div className="rounded-2xl bg-cream-card p-4 ring-1 ring-border-warm">
            <div className="flex items-center gap-2 mb-3">
              <List className="h-4 w-4 text-green-primary" />
              <h3 className="text-sm font-semibold text-green-dark">Характеристики</h3>
            </div>
            <div className="space-y-3">
              {Object.entries(product.characteristics).map(([key, value]) => (
                <div key={key} className="border-b border-cream pb-2 last:border-0 last:pb-0">
                  <div className="text-xs font-semibold text-green-dark">{key}</div>
                  <div className="mt-1 text-sm text-green-muted">{value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Описание */}
        {product.description && (
          <div className="rounded-2xl bg-cream-card p-4 ring-1 ring-border-warm">
            <div className="flex items-center gap-2 mb-3">
              <Info className="h-4 w-4 text-green-primary" />
              <h3 className="text-sm font-semibold text-green-dark">Описание</h3>
            </div>
            <p className="text-sm text-green-muted leading-relaxed">{product.description}</p>
          </div>
        )}

        {/* Уход */}
        {product.care && (
          <div className="rounded-2xl bg-cream-card p-4 ring-1 ring-border-warm">
            <div className="flex items-center gap-2 mb-3">
              <Droplets className="h-4 w-4 text-green-primary" />
              <h3 className="text-sm font-semibold text-green-dark">Уход</h3>
            </div>
            <p className="text-sm text-green-muted leading-relaxed">{product.care}</p>
          </div>
        )}
      </div>

      {/* Футер: «В корзину» закреплён ВСЕГДА снизу экрана (fixed), не в конце скролла */}
      <footer className="shrink-0 border-t border-border-warm bg-cream/95 px-4 py-3 backdrop-blur-xl">
        {notifError && (
          <div className="mb-2 rounded-lg bg-[#D64545]/10 p-3 text-center text-sm text-[#D64545]">
            {notifError}
          </div>
        )}
        <div className="flex gap-2">
          {isOutOfStock ? (
            <button
              onClick={handleNotification}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold transition active:scale-[0.98] ${
                notified
                  ? 'bg-green-sage/20 text-green-primary ring-1 ring-green-sage'
                  : 'bg-yellow-soft/20 text-terracotta ring-1 ring-yellow-soft hover:bg-yellow-soft/30'
              }`}
            >
              <Bell className={`h-4 w-4 ${notified ? 'fill-green-primary' : ''}`} />
              {notified ? 'Уведомим' : 'Сообщить о поступлении'}
            </button>
          ) : quantity === 0 ? (
            <button
              onClick={handleAdd}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-terracotta py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-terracotta/90 active:scale-[0.98]"
            >
              <ShoppingBag className="h-4 w-4" />
              В корзину
            </button>
          ) : (
            <div className="flex flex-1 items-center justify-between rounded-xl bg-green-sage/20 p-1">
              <button
                onClick={handleDecrease}
                className="grid h-10 w-10 place-items-center rounded-lg bg-white text-green-primary shadow-sm active:scale-90"
              >
                <Minus className="h-5 w-5" />
              </button>
              <span className="text-lg font-bold text-green-primary">{quantity}</span>
              <button
                onClick={() => addToCart(product, 1, selected)}
                className="grid h-10 w-10 place-items-center rounded-lg bg-green-primary text-white shadow-sm active:scale-90"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}
